import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  AutocompleteState,
  BaseItem,
  createAutocomplete,
} from '@algolia/autocomplete-core'
import { parseAlgoliaHitHighlight } from '@algolia/autocomplete-preset-algolia'
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions'
// The order of these imports is important for the CSS to work properly.
import '@algolia/autocomplete-theme-classic'

import SearchIcon from '@icons/Search'
import { suggetionsIndexName, searchClient } from '@models/Algolia'
import { useTranslation } from '@models/Translation'

import * as style from './style.module.sass'

type AutocompleteProps = {
  handleQueryChange: (query: string) => void
  initialState?: Partial<AutocompleteState<BaseItem>>
  disableInput?: boolean
}

const Autocomplete: React.FC<AutocompleteProps> = ({
  handleQueryChange,
  initialState,
  disableInput = false,
}) => {
  const { t } = useTranslation()

  const [autocompleteState, setAutocompleteState] =
    useState<AutocompleteState<BaseItem>>()
  const inputRef = useRef<HTMLInputElement>(null)
  const onComposition = useRef(false)

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus()
      // SideMenuでのアニメーションが終わるまで待つ
    }, 1500)
  }, [])

  const plugins = useMemo(() => {
    const querySuggestions = createQuerySuggestionsPlugin({
      searchClient,
      indexName: suggetionsIndexName,
      transformSource: ({ source }) => ({
        ...source,
        onSelect: ({ item }) => {
          handleQueryChange(item.query)
          inputRef.current?.blur()
        },
      }),
    })

    return [querySuggestions]
  }, [handleQueryChange])

  const autocomplete = useMemo(
    () =>
      createAutocomplete({
        onStateChange({ state }) {
          setAutocompleteState(state)
        },
        insights: true,
        placeholder: t('search.byKeyword'),
        onSubmit: ({ state }) => {
          handleQueryChange(state.query)
        },
        plugins,
        initialState,
      }),
    [handleQueryChange, initialState, plugins],
  )

  return (
    // There are type errors in the props expansion, but it's not resolved easily, we leave it as is for now.
    // https://www.algolia.com/doc/ui-libraries/autocomplete/guides/creating-a-renderer/#using-prop-getters
    <div className="aa-Autocomplete" {...autocomplete.getRootProps({})}>
      <form
        className={`aa-Form ${style.form}`}
        onBlur={() => {
          autocomplete.setIsOpen(false)
        }}
        {...autocomplete.getFormProps({ inputElement: inputRef.current })}
      >
        <div className="aa-InputWrapperPrefix">
          <button type="submit" className={style.button}>
            <SearchIcon />
          </button>
        </div>
        <div className="aa-InputWrapper">
          <input
            ref={inputRef}
            className={`aa-Input ${style.input}`}
            {...autocomplete.getInputProps({})}
            onKeyDownCapture={event => {
              if (
                onComposition.current &&
                (event.key === 'ArrowDown' || event.key === 'ArrowUp')
              ) {
                event.stopPropagation()
              }
            }}
            onCompositionStart={() => {
              onComposition.current = true
            }}
            onCompositionEnd={() => {
              onComposition.current = false
            }}
            disabled={disableInput}
          />
          {autocompleteState?.isOpen && (
            <div
              className={`aa-Panel ${style.panel}`}
              {...autocomplete.getPanelProps({})}
            >
              {autocompleteState.collections.map((collection, index) => {
                const { source, items } = collection
                return (
                  <div key={`source-${index}`} className="aa-Source">
                    {items.length > 0 && (
                      <ul className="aa-List" {...autocomplete.getListProps()}>
                        {items.map(item => (
                          <li
                            key={item.objectID as string}
                            className="aa-Item"
                            {...autocomplete.getItemProps({
                              item,
                              source,
                            })}
                          >
                            <Highlight hit={item} attribute="query" />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </form>
    </div>
  )
}

export default Autocomplete

const Highlight: React.FC<{ hit: BaseItem; attribute: string }> = ({
  hit,
  attribute,
}) => (
  <div>
    {parseAlgoliaHitHighlight({ hit, attribute }).map(
      ({ value, isHighlighted }, index) =>
        isHighlighted ? <strong key={index}>{value}</strong> : value,
    )}
  </div>
)
